import React, { useEffect, useState } from "react";
import { Dialog } from "@material-ui/core";
import { OrderStatus, PriceDetail } from "../../../../../../models/Order";
import { ReopenCancelledOrderByAdmin } from "../../../../../../services/OrderService";

const NOTE_MIN = 3;
const NOTE_MAX = 500;

function doneMessage(id: number, status: number) {
	if (status === OrderStatus.Reserved)
		return `Booking #${id} is Reserved again. The customer and station got the confirmation email with the original PIN.`;
	if (status === OrderStatus.NoShow)
		return `Booking #${id} is now No-Show (PIN expired). When the customer arrives, use Verify to confirm it. No email was sent.`;
	return `Booking #${id} is Confirmed again. The customer and station got the confirmation email.`;
}

/** Reopen a booking that is "Cancelled · No refund" (Quân 26/10/2026). No new PIN is issued. */
export default function ReopenBookingDialog(props: {
	open: boolean;
	order: PriceDetail;
	onClose: () => void;
	onDone: (message: string) => void;
}) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [note, setNote] = useState("");

	useEffect(() => {
		if (props.open) {
			setBusy(false);
			setError("");
			setNote("");
		}
	}, [props.open]);

	const o = props.order;
	const customer = (o as any).customer?.email || (o as any).user?.email || o.email || "—";
	const noteOk = note.trim().length >= NOTE_MIN;

	const submit = async () => {
		if (busy || !o.id || !noteOk) return;
		setBusy(true);
		setError("");
		try {
			const r = await ReopenCancelledOrderByAdmin(o.id, note.trim());
			props.onDone(doneMessage(o.id, Number(r?.status)));
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.message || "Reopen failed — nothing was changed.");
			setBusy(false);
		}
	};

	return (
		<Dialog open={props.open} onClose={busy ? undefined : props.onClose} maxWidth="xs" fullWidth>
			<div style={{ padding: 24, fontSize: 14, color: "#111827" }}>
				<div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Reopen booking #{o.id}</div>
				<div style={{ color: "#6B7280", marginBottom: 16 }}>
					This booking was cancelled without a refund. Reopen it so the customer can still use it?
				</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "100px 1fr",
						rowGap: 6,
						padding: 12,
						borderRadius: 10,
						background: "#F9FAFB",
						marginBottom: 14,
					}}>
					<span style={{ color: "#6B7280" }}>Customer</span>
					<span style={{ wordBreak: "break-all" }}>{customer}</span>
					<span style={{ color: "#6B7280" }}>Payment</span>
					<span>Kept as is — no new charge, original payment date unchanged</span>
				</div>
				<div style={{ color: "#4B5563", marginBottom: 12, lineHeight: 1.5 }}>
					The booking goes back to:
					<ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
						<li>
							<b>Reserved</b> if its PIN is still valid — the customer keeps the original PIN; confirmation email is
							sent again.
						</li>
						<li>
							<b>No-Show</b> if the PIN has expired (3 days after pick-up) — no email; use <b>Verify</b> when the
							customer arrives.
						</li>
						<li>
							<b>Confirmed</b> if the customer was already verified.
						</li>
					</ul>
					<div style={{ marginTop: 8 }}>
						Customer coming on another date? Reopen first, then change the dates with <b>Sửa</b>.
					</div>
				</div>
				<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }} htmlFor="reopen-note">
					Reason (required)
				</label>
				<textarea
					id="reopen-note"
					value={note}
					maxLength={NOTE_MAX}
					onChange={(e) => setNote(e.target.value)}
					rows={3}
					placeholder="e.g. Cancelled by mistake — customer still wants to store bags"
					style={{
						width: "100%",
						boxSizing: "border-box",
						padding: 10,
						borderRadius: 8,
						border: "1px solid #D1D5DB",
						fontFamily: "inherit",
						fontSize: 14,
						resize: "vertical",
					}}
				/>
				{error && (
					<div
						role="alert"
						style={{
							background: "#FEF2F2",
							color: "#B91C1C",
							borderRadius: 10,
							padding: "10px 12px",
							margin: "12px 0 0",
							lineHeight: 1.45,
						}}>
						{error}
					</div>
				)}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
					<button
						type="button"
						onClick={props.onClose}
						disabled={busy}
						style={{
							padding: "10px 16px",
							borderRadius: 8,
							border: "1px solid #D1D5DB",
							background: "#fff",
							cursor: busy ? "not-allowed" : "pointer",
							fontWeight: 600,
						}}>
						Close
					</button>
					<button
						type="button"
						onClick={submit}
						disabled={busy || !noteOk}
						style={{
							padding: "10px 16px",
							borderRadius: 8,
							border: "none",
							fontWeight: 700,
							cursor: busy || !noteOk ? "not-allowed" : "pointer",
							color: "#fff",
							background: busy || !noteOk ? "#9CA3AF" : "#0F766E",
						}}>
						{busy ? "Working…" : "Reopen booking"}
					</button>
				</div>
			</div>
		</Dialog>
	);
}
